"use client";

import { useState } from "react";
import { CheckCircle, Lock, Mail, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { anonSessionHeader } from "@/lib/anonSession";
import { toast } from "sonner";

// Backend keyword shape — the LLM emits structured objects with metadata.
// Older analyses or fallback paths may emit plain strings, so accept both.
export type KeywordEntry = string | {
  keyword: string;
  found_in?: string;
  context?: string;
  weight?: string;
};
function keywordText(kw: KeywordEntry): string {
  if (typeof kw === "string") return kw;
  return kw?.keyword ?? "";
}

// Shape returned by POST /api/v1/ats/guest-analyze (free-filtered via
// _build_ats_response on the backend). Field names match the backend's
// existing ATS contract used by the authenticated detail page.
export interface RealATSTeaserData {
  id: string;
  resume_id: string;
  score: number;
  matched_keywords: KeywordEntry[];
  summary: string;
  industry?: string | null;
  hidden_count?: {
    missing_keywords?: number;
    section_scores?: number;
    suggestions?: number;
    [key: string]: number | undefined;
  };
  has_more_premium?: boolean;
  is_guest?: boolean;
  /** Cookieless funnel: anon session id to persist + echo on claim/signup. */
  anonymous_session_id?: string;
}
interface Props {
  data: RealATSTeaserData;
  onSignupClick?: () => void;
}
const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
export function RealATSTeaser({
  data,
  onSignupClick
}: Props) {
  const {
    locale
  } = useTranslation();
  const fr = locale === "fr";
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast.error(fr ? "Email invalide" : "Invalid email");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/ats/email-claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...anonSessionHeader()
        },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          analysis_id: data.id
        })
      });
      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}));
        toast.error(errorBody?.detail?.message || (fr ? "Erreur. Réessaie." : "Error. Try again."));
        return;
      }
      setSent(true);
    } catch (err) {
      console.error("email-claim failed:", err);
      toast.error(fr ? "Erreur réseau. Réessaie." : "Network error. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Defensive: if score is missing/non-numeric, fall back to 0 to avoid NaN.
  const numericScore = typeof data.score === "number" && Number.isFinite(data.score) ? data.score : 0;
  const scoreColor = numericScore >= 70 ? "text-emerald-500" : numericScore >= 50 ? "text-amber-500" : "text-rose-500";
  return <div className="space-y-6">
      {/* SCORE — fully visible */}
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-6">
          <div className={`text-6xl font-bold tabular-nums ${scoreColor}`}>
            {Math.round(numericScore)}
            <span className="text-2xl text-muted-foreground">/100</span>
          </div>
          <div className="flex-1">
            <h3 className="mb-1 text-lg font-semibold">
              {fr ? "Ton score ATS" : "Your ATS score"}
            </h3>
            <p className="text-sm text-muted-foreground">{data.summary}</p>
          </div>
        </div>
      </div>

      {/* MATCHED KEYWORDS — visible */}
      {data.matched_keywords.length > 0 && <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            <CheckCircle className="h-4 w-4 text-emerald-500" />
            {fr ? "Mots-clés trouvés dans ton CV" : "Keywords matched in your CV"}
          </h3>
          <div className="flex flex-wrap gap-2">
            {data.matched_keywords.slice(0, 12).map((kw, idx) => {
          const text = keywordText(kw);
          if (!text) return null;
          return <span key={`${text}-${idx}`} className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                  {text}
                </span>;
        })}
          </div>
        </div>}

      {/* BLURRED SECTIONS + EMAIL CTA */}
      <div className="relative rounded-2xl border border-border bg-card p-6">
        {/* Blurred content underneath */}
        <div className="pointer-events-none select-none space-y-4 blur-md">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {fr ? "Mots-clés manquants" : "Missing keywords"} (
            {data.hidden_count?.missing_keywords ?? 0})
          </h3>
          <div className="flex flex-wrap gap-2">
            {Array.from({
            length: Math.min(data.hidden_count?.missing_keywords ?? 6, 8)
          }).map((_, i) => <span key={i} className="rounded-full bg-muted px-3 py-1 text-xs">
                  ████████
                </span>)}
          </div>
          <h3 className="mt-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {fr ? "Scores par section" : "Section scores"}
          </h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {Array.from({
            length: 4
          }).map((__0, i_0) => <div key={i_0} className="rounded-lg border border-border p-3">
                <div className="text-2xl font-bold">██</div>
                <div className="text-xs text-muted-foreground">████████</div>
              </div>)}
          </div>
          <h3 className="mt-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {fr ? "Recommandations détaillées" : "Detailed recommendations"}
          </h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            <li>███████████████████████████</li>
            <li>██████████████████████</li>
            <li>██████████████████████████████</li>
          </ul>
        </div>

        {/* Overlay form */}
        <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-gradient-to-b from-background/40 via-background/85 to-background backdrop-blur-[2px]">
          {sent ? <div className="mx-auto max-w-md p-6 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
                <Mail className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="mb-2 text-xl font-semibold">
                {fr ? "Vérifie ta boîte mail" : "Check your inbox"}
              </h3>
              <p className="mb-4 text-sm text-muted-foreground">
                {fr ? `On t'a envoyé un lien sur ${email}. Clique dessus pour voir ton rapport complet.` : `We sent a link to ${email}. Click it to see your full report.`}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {fr ? "Pas reçu ? Vérifie tes spams ou " : "Not received? Check your spam or "}
                <button onClick={() => setSent(false)} className="text-primary underline-offset-4 hover:underline">
                  {fr ? "réessaie" : "try again"}
                </button>
              </p>
            </div> : <div className="mx-auto w-full max-w-md p-6">
              <form onSubmit={handleSubmit}>
              <div className="mb-4 flex items-center gap-2">
                <Lock className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                  {fr ? "Rapport complet" : "Full report"}
                </span>
              </div>
              <h3 className="mb-2 text-xl font-semibold">
                {fr ? "Entre ton email pour voir le rapport complet" : "Enter your email to see the full report"}
              </h3>
              <p className="mb-4 text-sm text-muted-foreground">
                {fr ? "On t'envoie un lien magique pour débloquer ton analyse + créer ton compte en 1 click." : "We send a magic link to unlock your analysis + create your account in 1 click."}
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input type="email" required placeholder={fr ? "ton@email.com" : "you@email.com"} value={email} onChange={e_0 => setEmail(e_0.target.value)} disabled={isSubmitting} className="flex-1" />
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {fr ? "Envoi…" : "Sending…"}
                    </> : <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      {fr ? "Voir mon rapport" : "See my report"}
                    </>}
                </Button>
              </div>
              <p className="mt-3 text-[10px] text-muted-foreground">
                {fr ? "Pas de spam. En cliquant tu acceptes nos CGU + politique de confidentialité." : "No spam. By clicking you accept our Terms + Privacy Policy."}
              </p>
              </form>
              <div className="my-4 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">{fr ? "ou" : "or"}</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <GoogleSignInButton />
            </div>}
        </div>
      </div>
    </div>;
}